type PollResponse = {
  refreshing?: boolean;
  retry_after_ms?: number;
};

const POLL_DEADLINE_MS = 60_000;
const deadlines = new Map<string, number>();

export const analyticsWarmingError = 'Analytics is still warming. Retry the request in a moment.';

export function clearAnalyticsPoll(scope: string): void {
  deadlines.delete(scope);
}

export function observeAnalyticsResponse<T extends PollResponse>(
  scope: string,
  response: T,
  pending = response.refreshing === true,
): T {
  if (!pending) {
    deadlines.delete(scope);
    return response;
  }
  const deadline = deadlineFor(scope);
  if (Date.now() >= deadline) {
    deadlines.delete(scope);
    throw new Error(analyticsWarmingError);
  }
  return response;
}

export function analyticsPollInterval(
  scope: string,
  response: PollResponse | undefined,
  hasError: boolean,
  pending = response?.refreshing === true,
): number | false {
  if (hasError || !pending) {
    deadlines.delete(scope);
    return false;
  }
  const remaining = deadlineFor(scope) - Date.now();
  // One final poll at the deadline either receives the result or raises a visible error.
  return Math.max(1, Math.min(Math.max(response?.retry_after_ms ?? 2_000, 500), 10_000, remaining));
}

function deadlineFor(scope: string): number {
  const existing = deadlines.get(scope);
  if (existing !== undefined) return existing;
  if (deadlines.size >= 256) {
    const oldest = deadlines.keys().next().value;
    if (oldest !== undefined) deadlines.delete(oldest);
  }
  const deadline = Date.now() + POLL_DEADLINE_MS;
  deadlines.set(scope, deadline);
  return deadline;
}
